import { Fragment, useEffect, useRef, useState, type FormEvent } from 'react';
import { Info, SendHorizontal } from 'lucide-react';
import type { MensagemSuporte } from '../api/tipos';
import { Botao, cx } from './ui';
import css from './Chat.module.css';

const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const DIA = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

/** Mensagens do mesmo autor com menos que isso de intervalo viram um bloco. */
const AGRUPAR_MS = 5 * 60 * 1000;

function chaveDia(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** "Hoje", "Ontem" ou "9 de outubro de 2026" — separador entre dias. */
function rotuloDia(d: Date): string {
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(hoje.getDate() - 1);
  if (chaveDia(d) === chaveDia(hoje)) return 'Hoje';
  if (chaveDia(d) === chaveDia(ontem)) return 'Ontem';
  return DIA.format(d);
}

/**
 * Fio de mensagens de uma conversa de suporte, com a caixa de resposta.
 *
 * O mesmo componente serve o cliente e o admin: muda só qual lado é "eu"
 * (alinhado à direita) — assim as duas telas não divergem no primeiro ajuste.
 */
export function Chat({
  mensagens,
  euSou,
  nomeOutroLado,
  aoEnviar,
  aviso,
  preencher = false,
}: {
  mensagens: MensagemSuporte[];
  euSou: MensagemSuporte['autor'];
  nomeOutroLado: string;
  /** Lança em caso de erro; o texto digitado fica na caixa. */
  aoEnviar: (texto: string) => Promise<void>;
  /** Linha discreta acima da caixa (ex.: "conversa encerrada"). */
  aviso?: string;
  /**
   * `true` ocupa a altura do contêiner (que precisa ter altura definida), com
   * a caixa de resposta presa embaixo. Sem isso, o fio tem altura máxima
   * própria e a página rola normalmente.
   */
  preencher?: boolean;
}) {
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const fio = useRef<HTMLDivElement>(null);
  const caixa = useRef<HTMLTextAreaElement>(null);

  // Rola para a última mensagem quando chega uma nova — não a cada polling.
  // Rola o próprio fio (e não `scrollIntoView`), para não arrastar a página
  // inteira junto quando o chat está no meio dela.
  const total = mensagens.length;
  useEffect(() => {
    const el = fio.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [total]);

  // A caixa cresce com o texto até um limite, como em qualquer mensageiro.
  useEffect(() => {
    const el = caixa.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [texto]);

  async function enviar(e?: FormEvent) {
    e?.preventDefault();
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true);
    setErro(null);
    try {
      await aoEnviar(t);
      setTexto('');
    } catch (err) {
      setErro(err instanceof Error ? err.message : 'Falha ao enviar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className={cx(css.chat, preencher && css.preencher)}>
      <div ref={fio} className={css.fio} role="log" aria-label="Mensagens da conversa">
        {mensagens.map((m, i) => {
          const minha = m.autor === euSou;
          const quando = new Date(m.createdAt);
          const anterior = mensagens[i - 1];
          const seguinte = mensagens[i + 1];
          const novoDia =
            !anterior || chaveDia(new Date(anterior.createdAt)) !== chaveDia(quando);
          const continuaBloco =
            !!seguinte &&
            seguinte.autor === m.autor &&
            chaveDia(new Date(seguinte.createdAt)) === chaveDia(quando) &&
            new Date(seguinte.createdAt).getTime() - quando.getTime() < AGRUPAR_MS;
          const abreBloco =
            novoDia ||
            !anterior ||
            anterior.autor !== m.autor ||
            quando.getTime() - new Date(anterior.createdAt).getTime() >= AGRUPAR_MS;

          return (
            <Fragment key={m.id}>
              {novoDia && (
                <div className={css.dia} role="separator">
                  <span>{rotuloDia(quando)}</span>
                </div>
              )}
              <div
                className={cx(
                  css.mensagem,
                  minha ? css.minha : css.deles,
                  abreBloco && css.abreBloco,
                )}
              >
                {abreBloco && !minha && <span className={css.autor}>{nomeOutroLado}</span>}
                <div className={css.balao}>{m.texto}</div>
                {!continuaBloco && (
                  <time className={css.hora} dateTime={m.createdAt} title={quando.toLocaleString('pt-BR')}>
                    {minha ? 'Você · ' : ''}
                    {HORA.format(quando)}
                  </time>
                )}
              </div>
            </Fragment>
          );
        })}
      </div>

      <div className={css.rodape}>
        {aviso && (
          <p className={css.aviso}>
            <Info aria-hidden="true" />
            {aviso}
          </p>
        )}

        <form onSubmit={(e) => void enviar(e)} className={css.composicao}>
          <textarea
            ref={caixa}
            aria-label="Sua mensagem"
            className={css.caixa}
            rows={1}
            value={texto}
            maxLength={4000}
            placeholder="Escreva sua mensagem…"
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              // Enter envia; Shift+Enter quebra linha — o atalho de todo chat.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void enviar();
              }
            }}
          />
          <Botao
            type="submit"
            soIcone
            icone={<SendHorizontal />}
            aria-label="Enviar mensagem"
            title="Enviar (Enter)"
            carregando={enviando}
            disabled={!texto.trim()}
          />
        </form>
        {erro ? (
          <span role="alert" className={css.erro}>
            {erro}
          </span>
        ) : (
          <span className={css.dica}>Enter envia · Shift+Enter quebra a linha</span>
        )}
      </div>
    </div>
  );
}
